import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../services/apiClient';
import { MEDICAL_TERMINOLOGY } from '../constants/medicalTerminology';
import { ImageType } from '../types';

const MEDICAL_SYSTEMS = MEDICAL_TERMINOLOGY.MEDICAL_SYSTEMS;

const URGENCY_LEVELS = [
  { value: 'normal', label: 'Κανονική' },
  { value: 'low', label: 'Εκπαιδευτικό' },
  { value: 'high', label: 'Υψηλή' },
  { value: 'urgent', label: 'Επείγον' }
];

const IMAGE_TYPES: ImageType[] = [
  'he',
  'histochemical', 
  'immunohistochemistry',
  'molecular',
  'gross'
];

const IMAGE_TYPE_LABELS: Record<ImageType, string> = {
  'he': 'Hematoxylin/Eosin (H&E)',
  'histochemical': 'Ιστοχημικές χρώσεις',
  'immunohistochemistry': 'Ανοσοϊστοχημεία',
  'molecular': 'Μοριακή',
  'gross': 'Μακροσκοπική'
};

const HISTOCHEMICAL_STAINS = [
  'Periodic Acid-Schiff (PAS)',
  'Alcian Blue',
  "Masson's Trichrome",
  'Reticulin Stain (Gomori)',
  'Ziehl-Neelsen (AFB)',
  'Giemsa',
  'Congo Red',
  'Oil Red O / Sudan Stains',
  "Grocott's Methenamine Silver (GMS)",
  'Gram Stain',
  'Warthin-Starry'
];

export function EditCasePage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    patient_age: '',
    patient_gender: '',
    clinical_history: '',
    case_type: '',
    tissue_type: '',
    urgency_level: 'normal',
    macroscopic_description: '',
    microscopic_description: ''
  });
  
  // Image management state
  const [existingImages, setExistingImages] = useState<Array<{
    id: string;
    url: string;
    type: ImageType;
    staining?: string;
    magnification?: string;
  }>>([]);
  const [newImages, setNewImages] = useState<File[]>([]);
  const [newImageTypes, setNewImageTypes] = useState<ImageType[]>([]);
  const [newImageStaining, setNewImageStaining] = useState<string[]>([]);
  const [newImageMagnification, setNewImageMagnification] = useState<string[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (caseId) {
      fetchCaseData();
    }
  }, [caseId]);

  const fetchCaseData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/cases/${caseId}`);
      const caseData = response.data.data.case;
      
      // Check if user can edit this case
      if (caseData.userId !== user?.id && user?.role !== 'admin' && user?.role !== 'moderator') {
        navigate('/cases', { replace: true });
        return;
      }

      setFormData({
        title: caseData.title || '',
        description: caseData.description || '',
        patient_age: caseData.patientAge?.toString() || '',
        patient_gender: caseData.patientGender || '',
        clinical_history: caseData.clinicalHistory || '',
        case_type: caseData.caseType || '',
        tissue_type: caseData.tissueType || '',
        urgency_level: caseData.urgencyLevel || 'normal',
        macroscopic_description: caseData.macroscopicDescription || '',
        microscopic_description: caseData.microscopicDescription || ''
      });
      
      // Load existing images
      if (caseData.images && caseData.images.length > 0) {
        setExistingImages(caseData.images.map((img: any) => ({
          id: img.id,
          url: img.imageUrl,
          type: img.type || img.imageType || 'he',
          staining: img.staining || img.stainingInfo,
          magnification: img.magnification
        })));
      }
    } catch (err: any) {
      console.error('Error fetching case:', err);
      setError('Σφάλμα φόρτωσης περιστατικού');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Image management functions
  const handleImageUpload = useCallback((files: FileList) => {
    const validFiles = Array.from(files).filter(file => {
      if (!file.type.startsWith('image/')) return false;
      if (file.size > 100 * 1024 * 1024) return false; // 100MB limit
      return true;
    });

    if (validFiles.length > 0) {
      setNewImages(prev => [...prev, ...validFiles]);
      setNewImageTypes(prev => [...prev, ...validFiles.map(() => 'he' as ImageType)]);
      setNewImageStaining(prev => [...prev, ...validFiles.map(() => '')]);
      setNewImageMagnification(prev => [...prev, ...validFiles.map(() => '')]);
    }
  }, []);

  const removeNewImage = (index: number) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
    setNewImageTypes(prev => prev.filter((_, i) => i !== index));
    setNewImageStaining(prev => prev.filter((_, i) => i !== index));
    setNewImageMagnification(prev => prev.filter((_, i) => i !== index));
  };

  const updateNewImageType = (index: number, type: ImageType) => {
    setNewImageTypes(prev => prev.map((t, i) => i === index ? type : t));
    // Clear staining if not immunohistochemistry or histochemical
    if (type !== 'immunohistochemistry' && type !== 'histochemical') {
      setNewImageStaining(prev => prev.map((s, i) => i === index ? '' : s));
    }
  };

  const updateNewImageStaining = (index: number, staining: string) => {
    setNewImageStaining(prev => prev.map((s, i) => i === index ? staining : s));
  };

  const updateNewImageMagnification = (index: number, magnification: string) => {
    setNewImageMagnification(prev => prev.map((m, i) => i === index ? magnification : m));
  };

  const removeExistingImage = (imageId: string) => {
    setImagesToDelete(prev => [...prev, imageId]);
    setExistingImages(prev => prev.filter(img => img.id !== imageId));
  };

  const updateExistingImageType = (imageId: string, type: ImageType) => {
    setExistingImages(prev => prev.map(img => 
      img.id === imageId ? { 
        ...img, 
        type, 
        staining: (type !== 'immunohistochemistry' && type !== 'histochemical') ? undefined : img.staining 
      } : img
    ));
  };

  const updateExistingImageStaining = (imageId: string, staining: string) => {
    setExistingImages(prev => prev.map(img => 
      img.id === imageId ? { ...img, staining } : img
    ));
  };

  const updateExistingImageMagnification = (imageId: string, magnification: string) => {
    setExistingImages(prev => prev.map(img => 
      img.id === imageId ? { ...img, magnification } : img
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Τίτλος και περιγραφή είναι υποχρεωτικά');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      // Determine if we have new images or image changes
      const hasNewImages = newImages.length > 0;
      const hasImageChanges = imagesToDelete.length > 0 || existingImages.some(img => 
        img.type !== 'he' || img.staining || img.magnification
      );
      
      if (hasNewImages || hasImageChanges) {
        // Use FormData for multipart upload
        const formDataToSend = new FormData();
        
        // Add all text fields
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('macroscopic_description', formData.macroscopic_description || '');
        formDataToSend.append('microscopic_description', formData.microscopic_description || '');
        
        if (formData.patient_age) formDataToSend.append('patient_age', formData.patient_age);
        if (formData.patient_gender) formDataToSend.append('patient_gender', formData.patient_gender);
        if (formData.clinical_history) formDataToSend.append('clinical_history', formData.clinical_history);
        if (formData.case_type) formDataToSend.append('case_type', formData.case_type);
        if (formData.tissue_type) formDataToSend.append('tissue_type', formData.tissue_type);
        
        formDataToSend.append('urgency_level', formData.urgency_level);
        
        // Add new images
        newImages.forEach((file, index) => {
          formDataToSend.append('images', file);
          formDataToSend.append(`image_type_${index}`, newImageTypes[index]);
          if ((newImageTypes[index] === 'immunohistochemistry' || newImageTypes[index] === 'histochemical') && newImageStaining[index]) {
            formDataToSend.append(`image_staining_${index}`, newImageStaining[index]);
          }
          if (newImageMagnification[index]) {
            formDataToSend.append(`image_magnification_${index}`, newImageMagnification[index]);
          }
        });
        
        // Add existing image updates
        formDataToSend.append('existing_images', JSON.stringify(existingImages));
        formDataToSend.append('images_to_delete', JSON.stringify(imagesToDelete));

        const response = await fetch(`/api/v1/cases/${caseId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
            // Don't set Content-Type for FormData - browser will set it with boundary
          },
          body: formDataToSend
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Σφάλμα ενημέρωσης περιστατικού');
        }
      } else {
        // Use JSON for text-only updates
        const submitData = {
          ...formData,
          patient_age: formData.patient_age ? parseInt(formData.patient_age) : null
        };

        await apiClient.put(`/cases/${caseId}`, submitData);
      }
      
      // Redirect back to case detail
      navigate(`/cases/${caseId}`);
    } catch (err: any) {
      console.error('Error updating case:', err);
      setError(err.response?.data?.message || 'Σφάλμα ενημέρωσης περιστατικού');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Επεξεργασία Περιστατικού</h1>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Τίτλος Περιστατικού *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Συνοπτικός τίτλος του περιστατικού"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Περιγραφή *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Λεπτομερής περιγραφή του περιστατικού"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Σύστημα
              </label>
              <select
                name="case_type"
                value={formData.case_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Επιλέξτε σύστημα</option>
                {MEDICAL_SYSTEMS.map(system => (
                  <option key={system} value={system}>{system}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Εντόπιση βλάβης *
              </label>
              <input
                type="text"
                name="tissue_type"
                value={formData.tissue_type}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Περιγράψτε την εντόπιση της βλάβης"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ηλικία Ασθενούς
              </label>
              <input
                type="number"
                name="patient_age"
                value={formData.patient_age}
                onChange={handleInputChange}
                min="0"
                max="120"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="π.χ. 45"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Φύλο Ασθενούς
              </label>
              <select
                name="patient_gender"
                value={formData.patient_gender}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Επιλέξτε φύλο</option>
                <option value="male">Άνδρας</option>
                <option value="female">Γυναίκα</option>
                <option value="other">Άλλο</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Προτεραιότητα
              </label>
              <select
                name="urgency_level"
                value={formData.urgency_level}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {URGENCY_LEVELS.map(level => (
                  <option key={level.value} value={level.value}>{level.label}</option>
                ))}
              </select>
            </div>


            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Κλινικό Ιστορικό
              </label>
              <textarea
                name="clinical_history"
                value={formData.clinical_history}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Κλινικές πληροφορίες και ιστορικό ασθενούς"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Μακροσκοπική Περιγραφή
              </label>
              <textarea
                name="macroscopic_description"
                value={formData.macroscopic_description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Μακροσκοπικά ευρήματα"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Μικροσκοπική Περιγραφή
              </label>
              <textarea
                name="microscopic_description"
                value={formData.microscopic_description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Μικροσκοπικά ευρήματα και παρατηρήσεις"
              />
            </div>
          </div>

          {/* Image Management Section */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Διαχείριση Εικόνων</h3>
            
            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-700 mb-3">Υπάρχουσες Εικόνες</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {existingImages.map((image) => (
                    <div key={image.id} className="relative border border-gray-200 rounded-lg p-2">
                      <img
                        src={image.url}
                        alt="Εικόνα περιστατικού"
                        className="w-full h-32 object-cover rounded"
                      />
                      <div className="mt-2 space-y-2">
                        <select
                          value={image.type}
                          onChange={(e) => updateExistingImageType(image.id, e.target.value as ImageType)}
                          className="w-full text-xs border border-gray-300 rounded px-1 py-1"
                        >
                          {IMAGE_TYPES.map(type => (
                            <option key={type} value={type}>
                              {IMAGE_TYPE_LABELS[type]}
                            </option>
                          ))}
                        </select>
                        {image.type === 'histochemical' && (
                          <select
                            value={image.staining || ''}
                            onChange={(e) => updateExistingImageStaining(image.id, e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded px-1 py-1"
                          >
                            <option value="">Επιλογή χρώσης...</option>
                            {HISTOCHEMICAL_STAINS.map(stain => (
                              <option key={stain} value={stain}>
                                {stain}
                              </option>
                            ))}
                          </select>
                        )}
                        {image.type === 'immunohistochemistry' && (
                          <input
                            type="text"
                            value={image.staining || ''}
                            onChange={(e) => updateExistingImageStaining(image.id, e.target.value)}
                            placeholder="Χρώση"
                            className="w-full text-xs border border-gray-300 rounded px-1 py-1"
                          />
                        )}
                        <input
                          type="text"
                          value={image.magnification || ''}
                          onChange={(e) => updateExistingImageMagnification(image.id, e.target.value)}
                          placeholder="π.χ. χ10, χ40, χ100"
                          className="w-full text-xs border border-gray-300 rounded px-1 py-1"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExistingImage(image.id)}
                        className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Images */}
            {newImages.length > 0 && (
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-700 mb-3">Νέες Εικόνες</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {newImages.map((file, index) => (
                    <div key={index} className="relative border border-gray-200 rounded-lg p-2">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Νέα εικόνα ${index + 1}`}
                        className="w-full h-32 object-cover rounded"
                      />
                      <div className="mt-2 space-y-2">
                        <select
                          value={newImageTypes[index]}
                          onChange={(e) => updateNewImageType(index, e.target.value as ImageType)}
                          className="w-full text-xs border border-gray-300 rounded px-1 py-1"
                        >
                          {IMAGE_TYPES.map(type => (
                            <option key={type} value={type}>
                              {IMAGE_TYPE_LABELS[type]}
                            </option>
                          ))}
                        </select>
                        {newImageTypes[index] === 'histochemical' && (
                          <select
                            value={newImageStaining[index] || ''}
                            onChange={(e) => updateNewImageStaining(index, e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded px-1 py-1"
                          >
                            <option value="">Επιλογή χρώσης...</option>
                            {HISTOCHEMICAL_STAINS.map(stain => (
                              <option key={stain} value={stain}>
                                {stain}
                              </option>
                            ))}
                          </select>
                        )}
                        {newImageTypes[index] === 'immunohistochemistry' && (
                          <input
                            type="text"
                            value={newImageStaining[index] || ''}
                            onChange={(e) => updateNewImageStaining(index, e.target.value)}
                            placeholder="Χρώση"
                            className="w-full text-xs border border-gray-300 rounded px-1 py-1"
                          />
                        )}
                        <input
                          type="text"
                          value={newImageMagnification[index] || ''}
                          onChange={(e) => updateNewImageMagnification(index, e.target.value)}
                          placeholder="π.χ. χ10, χ40, χ100"
                          className="w-full text-xs border border-gray-300 rounded px-1 py-1"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeNewImage(index)}
                        className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Images Button */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <div className="mx-auto w-12 h-12 text-gray-400 mb-4">
                📷
              </div>
              <p className="text-lg font-medium text-gray-900 mb-2">
                Προσθήκη Εικόνων
              </p>
              <p className="text-gray-600 mb-4">
                Σύρτε και αφήστε ή κάντε κλικ για επιλογή αρχείων
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Επιλογή Εικόνων
              </button>
              <p className="text-xs text-gray-500 mt-2">
                Μέγιστο 100MB η κάθε εικόνα
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`/cases/${caseId}`)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Ακύρωση
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <LoadingSpinner size="sm" /> : 'Αποθήκευση Αλλαγών'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}